import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useContext, useRef } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import PersonIcon from '@material-ui/icons/Person';
import WarningIcon from '@material-ui/icons/Warning';
import { SnackbarContext, SnackbarVariant, Tooltip } from '@eas/common-web';
import { PlannerContext } from '@modules/planner/planner-context';
import { UnitMenu, UnitMenuHandle } from '@modules/planner/unit-menu/unit-menu';
import { Absence } from '@models';
import { EvidenceApiUrl } from '@enums';
import { CalendarContext } from '../../calendar-context';
import { CalendarDataType } from '../../calendar-types';
import { useStyles } from './legend-styles';

interface UnitProps {
  item: CalendarDataType;
  style: React.CSSProperties;
}

export function Unit({ item, style }: UnitProps) {
  const classes = useStyles();

  const menuRef = useRef<UnitMenuHandle>(null);

  const { calendarSource } = useContext(PlannerContext);
  const { settings } = useContext(CalendarContext);
  const { showSnackbar } = useContext(SnackbarContext);

  const absences: Absence[] = item.absences ?? [];

  const absenceTooltip = absences
    .filter((absence) => absence.allowed)
    .map((absence) => {
      let dateValue = '';
      if (absence.fromDate) {
        dateValue = dayjs(absence.fromDate).format('DD. MM. YYYY HH:mm');
      }
      if (absence.toDate) {
        dateValue += ` - ${(dateValue = dayjs(absence.toDate).format(
          'DD. MM. YYYY HH:mm'
        ))}`;
      }

      return `${absence.absenceType?.name} (${dateValue})`;
    })
    .join(', ');

  const tooltipMessages = [
    item.department?.isCalamity &&
      'Pracovisko sa aktuálne nachádza v stave kalamity',
    !!absenceTooltip && `Absencie: ${absenceTooltip}`,
  ].filter(Boolean);

  return (
    <div
      className={classes.legendListItem}
      style={style}
      onContextMenu={(e) => {
        e.preventDefault();
        menuRef.current?.open(e);
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flex: 3,
        }}
      >
        <div>
          {!!tooltipMessages.length && (
            <Tooltip
              title={
                <div style={{ whiteSpace: 'pre-line' }}>
                  {tooltipMessages.join('\n')}
                </div>
              }
            >
              <WarningIcon className={classes.legendIcon} fontSize="small" />
            </Tooltip>
          )}
          {item.unitType === 'user' && (
            <PersonIcon className={classes.legendIcon} fontSize="small" />
          )}
          {item.unitType === 'mobileUnit' && (
            <DirectionsCarIcon
              className={classes.legendIcon}
              fontSize="small"
            />
          )}
        </div>
        {item.name}
      </div>
      {!settings.legendCollapsed && (
        <>
          {item.unitType === 'mobileUnit' ? (
            <>
              <div style={{ flex: 2 }}> {_.get(item, 'callFirst')}</div>
              <div style={{ flex: 1 }}>
                <Checkbox
                  className={classes.legendIcon}
                  checked={_.get(item, 'morningFailureSuitable')}
                  color="primary"
                  onChange={async (e) => {
                    const checked = Boolean(e.currentTarget.checked);
                    const response = await fetch(
                      `${EvidenceApiUrl.MOBILE_UNITS}/${item.id}`
                    );
                    if (!response.ok) {
                      showSnackbar(
                        'Atribút sa nepodarilo zmeniť.',
                        SnackbarVariant.ERROR
                      );
                      return;
                    }
                    const data = await response.json();
                    if (!data) {
                      showSnackbar(
                        'Atribút sa nepodarilo zmeniť.',
                        SnackbarVariant.ERROR
                      );
                      return;
                    }

                    await fetch(`${EvidenceApiUrl.MOBILE_UNITS}/${item.id}`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        ...data,
                        morningFailureSuitable: checked,
                      }),
                    });
                    calendarSource?.yAxis?.refreshData?.();
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div style={{ flex: 2 }}> </div>
              <div style={{ flex: 1 }} />
            </>
          )}
        </>
      )}

      {!settings.legendCollapsed && (
        <div style={{ flex: 1 }}>
          <Checkbox
            className={classes.legendIcon}
            checked={_.get(item, 'customerSuitable')}
            color="primary"
            onChange={async (e) => {
              const checked = Boolean(e.currentTarget.checked);

              const baseUrl =
                item.unitType === 'mobileUnit'
                  ? EvidenceApiUrl.MOBILE_UNITS
                  : EvidenceApiUrl.USERS;

              const response = await fetch(`${baseUrl}/${item.id}`);
              if (!response.ok) {
                showSnackbar(
                  'Atribút sa nepodarilo zmeniť.',
                  SnackbarVariant.ERROR
                );
                return;
              }
              const data = await response.json();
              if (!data) {
                showSnackbar(
                  'Atribút sa nepodarilo zmeniť.',
                  SnackbarVariant.ERROR
                );
                return;
              }

              await fetch(`${baseUrl}/${item.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  ...data,
                  customerSuitable: checked,
                }),
              });
              calendarSource?.yAxis?.refreshData?.();
            }}
          />
        </div>
      )}

      <UnitMenu
        unit={{
          id: item.id,
          name: item.name,
          unitType: item.unitType,
          latitude: item.latitude,
          longitude: item.longitude,
          currentLocation: item.currentLocation,
          callFirst: item.callFirst,
        }}
        workOrders={item.workOrders}
        ref={menuRef}
      />
    </div>
  );
}
